import React, { useState } from 'react';
import { ChevronDown, FileDown, Loader2 } from 'lucide-react';
import {
    erzeugePlanungsdokumentation,
    planungsdokumentationDateiname,
} from '../../../services/pdf/generatorPlanungsdokumentation';
import { type PdfKontext, speicherePdf } from '../planungIntegration';
import { baueDokumentModell, sigePlanFuerDokument, type DokumentEingang } from './planungDokumentModell';
import { belegIds, belegTitelAus } from './belegTitel';
import { fetchProjektDokumente } from '../../../services/projektDokumenteService';
import { fetchSigePlanDokumente, type SigePlanDokumente } from '../../../services/sigePlanDokumenteService';

/**
 * Kopfknopf „Dokumentation (PDF)" der Planungsphase.
 *
 * Duenn: Modell bauen (`baueDokumentModell`), setzen
 * (`erzeugePlanungsdokumentation`), speichern (`speicherePdf`). Fehler stehen
 * als Text neben dem Knopf, kein Browser-Dialog.
 *
 * Erst beim Druck gelesen werden die Belegtitel der Ablage und die
 * mitgeltenden Dokumente des SiGe-Plans (dieselbe Lesefunktion wie
 * `useSigePlanDokumente`). Ob der Plan gelesen wurde, geht als `sigePlan` ins
 * Modell — Abschnitt 5.1 nennt ihn nur dann.
 *
 * Das kleine Menue am Pfeil schaltet die interne Bewertung zu. Vorgabe aus:
 * die Dokumentation kann nach aussen gehen, die Bewertung ist die
 * Einschaetzung des Koordinators. Ist der Schalter an, sagt es die
 * Beschriftung des Knopfs, der Dateiname (`_intern`) und die Fusszeile jeder
 * Seite — und nach jedem Druck steht er wieder auf aus: niemand soll die
 * interne Fassung versehentlich ein zweites Mal erzeugen und verschicken.
 */

const KNOPF = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed';

/** Was der Container liefert — SiGe-Plan und Belegtitel liest der Knopf beim Druck selbst. */
export type DokumentationsEingang = Omit<DokumentEingang, 'sigePlan' | 'sigeSchritte' | 'belegTitel'>;

interface Props {
    /** `null`, solange die Planungsphase nicht geladen ist. */
    eingang: DokumentationsEingang | null;
    pdf: PdfKontext;
    /** Gesetzt (z. B. „Grundlagen sind nicht gespeichert"): Knopf aus, Grund als Tooltip. */
    gesperrtGrund: string | null;
}

export const DokumentationKnopf: React.FC<Props> = ({ eingang, pdf, gesperrtGrund }) => {
    const [fehler, setFehler] = useState<string | null>(null);
    const [laeuft, setLaeuft] = useState(false);
    const [menueOffen, setMenueOffen] = useState(false);
    const [mitBewertung, setMitBewertung] = useState(false);

    const grund = gesperrtGrund || (eingang ? null : 'Die Planungsphase ist noch nicht geladen.');
    const aus = !!grund || laeuft;
    const titel = grund
        || (mitBewertung
            ? 'Dokumentation mit interner Bewertung erzeugen (nur für die eigene Akte)'
            : 'Dokumentation der gesamten Planungsphase als PDF erzeugen');

    const erzeugen = () => {
        if (!eingang || grund) return;
        // Die Fassung gilt fuer GENAU diesen Druck; danach steht der Schalter
        // wieder auf aus (unten im `finally`).
        const intern = mitBewertung;
        setFehler(null);
        setMenueOffen(false);
        setLaeuft(true);
        // Ein Takt Pause, damit der Ladezustand gezeichnet wird: der Satz
        // laeuft synchron und dauert bei grossen Katalogen spuerbar.
        window.setTimeout(async () => {
            try {
                // Belegtitel erst beim Druck aus der Ablage — fail-open: ohne
                // sie steht im PDF „Beleg in der Ablage".
                const ladeBelegTitel = async (): Promise<Record<string, string>> => {
                    if (!belegIds(eingang.fragen).length) return {};
                    try {
                        return belegTitelAus(await fetchProjektDokumente(eingang.phase.project_id), eingang.fragen);
                    } catch (e) {
                        console.warn('Belegtitel der Ablage konnten nicht gelesen werden.', e);
                        return {};
                    }
                };
                // Der SiGe-Plan: `fetchSigePlanDokumente` wirft nie und meldet
                // einen Lesefehler als `lesefehler`; scheitert der Aufruf doch,
                // gilt der Plan als nicht gelesen — nie als „nennt nichts".
                const ladeSigePlan = async (): Promise<SigePlanDokumente | null> => {
                    try {
                        return await fetchSigePlanDokumente(eingang.phase.project_id);
                    } catch (e) {
                        console.warn('Der SiGe-Plan konnte nicht gelesen werden.', e);
                        return null;
                    }
                };
                const [belegTitel, sige] = await Promise.all([ladeBelegTitel(), ladeSigePlan()]);
                const modell = baueDokumentModell(
                    { ...eingang, belegTitel, ...sigePlanFuerDokument(sige) },
                    { mitInternerBewertung: intern },
                );
                const gebaut = erzeugePlanungsdokumentation(modell, pdf);
                if (gebaut.status === 'fehler') { setFehler(gebaut.fehler); return; }
                const name = planungsdokumentationDateiname(
                    eingang.projekt.name || eingang.projekt.nummer || 'Projekt',
                    eingang.heuteIso,
                    { intern },
                );
                const gespeichert = speicherePdf(gebaut.wert, name);
                if (gespeichert.status === 'fehler') setFehler(gespeichert.fehler);
            } catch (e) {
                setFehler(`Die Dokumentation konnte nicht erzeugt werden: ${e instanceof Error ? e.message : String(e)}`);
            } finally {
                setLaeuft(false);
                setMitBewertung(false);
            }
        }, 0);
    };

    return (
        <div
            className="relative inline-flex items-center gap-2"
            onBlur={e => {
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setMenueOffen(false);
            }}
            onKeyDown={e => {
                if (e.key === 'Escape') setMenueOffen(false);
            }}
        >
            {/* Der Tooltip haengt an der Huelle: ein deaktivierter Knopf zeigt
                in manchen Browsern seinen eigenen `title` nicht. */}
            <span className="inline-flex" title={titel}>
                <button type="button" className={`${KNOPF} rounded-r-none`} disabled={aus} onClick={erzeugen}>
                    {laeuft
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <FileDown className="w-3.5 h-3.5" />}
                    {mitBewertung ? 'Dokumentation (PDF, intern)' : 'Dokumentation (PDF)'}
                </button>
                <button
                    type="button"
                    className={`${KNOPF} rounded-l-none border-l-0 px-1.5`}
                    disabled={aus}
                    aria-label="Optionen der Dokumentation"
                    aria-expanded={menueOffen}
                    onClick={() => setMenueOffen(o => !o)}
                >
                    <ChevronDown className="w-3.5 h-3.5" />
                </button>
            </span>

            {menueOffen && (
                <div className="absolute right-0 top-full z-20 mt-1 w-64 rounded-lg border border-slate-200 bg-white p-3 shadow-lg">
                    <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
                        <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={mitBewertung}
                            onChange={e => setMitBewertung(e.target.checked)}
                        />
                        <span>
                            <span className="font-bold">Mit interner Bewertung</span>
                            <span className="block text-slate-500">Nur für die eigene Akte, nicht zur Weitergabe an den Bauherrn.</span>
                        </span>
                    </label>
                </div>
            )}

            {fehler && <span role="alert" className="max-w-xs text-xs text-rose-700">{fehler}</span>}
        </div>
    );
};
